import type { ReactNode } from 'react';

import cn from 'classnames';

import { Flag } from 'ui/atoms/Flag/Flag';
import type { WhoCanBenefitItem } from 'ui/organisms/WhoCanBenefit/WhoCanBenefit.types';
import type { DifferenceItem } from 'ui/organisms/DifferenceSection/DifferenceSection.types';

import styles from './BrowserExtensionPage.module.scss';

type Translate = (key: string) => string;

const TURNOVER_BARS = [14, 17, 16, 21, 19, 26, 31, 29, 36, 42, 39, 48, 55, 52, 61, 47, 68, 79, 88, 100];

const getCrmRows = (t: Translate) => [
  { name: 'HubSpot', meta: t('Company · Contact · Association') },
  { name: 'Salesforce', meta: t('Account · Contact') },
  { name: 'Microsoft Dynamics', meta: t('Account · Contact') },
  { name: 'Global Database list', meta: t('Saved list · Export') },
];

const getExecutives = (t: Translate) => [
  {
    name: 'A. Whitcombe',
    role: t('Chief Executive Officer'),
    mail: 'a.whit•••@meridian-logistics.example',
    tel: '+44 20 •••• ••18',
  },
  {
    name: 'R. Osei',
    role: t('Chief Financial Officer'),
    mail: 'r.osei•••@meridian-logistics.example',
    tel: '+44 20 •••• ••24',
  },
  {
    name: 'L. Ferrante',
    role: t('Head of Procurement'),
    mail: 'l.ferr•••@meridian-logistics.example',
    tel: '+44 20 •••• ••41',
  },
];

const PanelSlide = ({ t }: { t: Translate }) => (
  <div className={styles.vid}>
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img
      className={styles.vidEl}
      src="/storage/img/browser-extension/why-step-01.webp"
      alt={t('The Global Database panel opening beside a company website')}
      decoding="async"
    />
  </div>
);

const OwnershipCard = ({ t }: { t: Translate }) => (
  <div className={styles.card}>
    <p className={styles.cardKicker}>{t('Registry ownership')}</p>
    <p className={styles.cardTitle}>Meridian Logistics Ltd</p>
    <p className={styles.cardMeta}>
      07734112 &middot; {t('United Kingdom')} &middot; {t('sample record')}
    </p>

    <div className={styles.tree}>
      <div className={cn(styles.treeRow, styles.treeD0)}>
        <span className={styles.treeName}>Meridian Holdings SA</span>
        <Flag code="lu" className={styles.treeFlag} />
        <span className={styles.treeMeta}>{t('ultimate parent')}</span>
      </div>

      <div className={cn(styles.treeRow, styles.treeD1)}>
        <span className={styles.railMark}>└</span>
        <span className={styles.treeName}>Meridian Logistics Ltd</span>
        <Flag code="gb" className={styles.treeFlag} />
        <span className={styles.treeMeta}>86.0%</span>
      </div>

      <div className={cn(styles.treeRow, styles.treeD2)}>
        <span className={styles.railMark}>└</span>
        <span className={styles.treeName}>Meridian Freight Services Ltd</span>
        <Flag code="gb" className={styles.treeFlag} />
        <span className={styles.treeMeta}>100.0%</span>
      </div>

      <div className={cn(styles.treeRow, styles.treeD2)}>
        <span className={styles.railMark}>└</span>
        <span className={styles.treeName}>Meridian Warehousing NV</span>
        <Flag code="nl" className={styles.treeFlag} />
        <span className={styles.treeMeta}>74.5%</span>
      </div>

      <div className={cn(styles.treeRow, styles.treeD1)}>
        <span className={styles.railMark}>└</span>
        <span className={styles.treeName}>Meridian Logistics Polska Sp. z o.o.</span>
        <Flag code="pl" className={styles.treeFlag} />
        <span className={styles.treeMeta}>100.0%</span>
      </div>
    </div>

    <p className={styles.cardFoot}>
      {t('Source: registry shareholding filings')} &middot; {t('synced daily')}
    </p>
  </div>
);

const FinancialsCard = ({ t }: { t: Translate }) => (
  <div className={styles.card}>
    <p className={styles.cardKicker}>{t('Filed turnover, GBP')}</p>

    <div className={styles.bars}>
      {TURNOVER_BARS.map((height, i) => (
        <b key={i} className={cn(i === TURNOVER_BARS.length - 1 && styles.barHi)} style={{ height: `${height}%` }} />
      ))}
    </div>

    <div className={styles.barsAxis}>
      <span>2005</span>
      <span>2024</span>
    </div>

    <div className={styles.cardRow}>
      <span>{t('Turnover 2024')}</span>
      <b>61,842,000</b>
    </div>

    <div className={styles.cardRow}>
      <span>{t('Gross profit')}</span>
      <b>14,207,000</b>
    </div>

    <div className={styles.cardRow}>
      <span>{t('Net assets')}</span>
      <b>9,336,000</b>
    </div>

    <div className={styles.cardRow}>
      <span>{t('Employees')}</span>
      <b>412</b>
    </div>

    <p className={styles.cardFoot}>
      {t('Sample record')} &middot; {t('20 years on file')}
    </p>
  </div>
);

const OfficersCard = ({ t }: { t: Translate }) => (
  <div className={styles.card}>
    <p className={styles.cardKicker}>{t('Officers and shareholders')}</p>
    <p className={styles.cardSub}>{t('Directors')}</p>

    <div className={styles.cardRow}>
      <span>A. Whitcombe &mdash; {t('Director')}</span>
      <b>{t('Appointed')} 2014-03-11</b>
    </div>

    <div className={styles.cardRow}>
      <span>R. Osei &mdash; {t('Director')}</span>
      <b>{t('Appointed')} 2019-07-02</b>
    </div>

    <div className={styles.cardRow}>
      <span>M. Halvorsen &mdash; {t('Secretary')}</span>
      <b>{t('Appointed')} 2016-01-20</b>
    </div>

    <p className={styles.cardSub}>{t('Registered shareholders')}</p>

    <div className={styles.cardRow}>
      <span>Meridian Holdings SA</span>
      <b>86.0%</b>
    </div>

    <div className={styles.cardRow}>
      <span>A. Whitcombe</span>
      <b>9.0%</b>
    </div>

    <div className={styles.cardRow}>
      <span>Meridian Employee Trust</span>
      <b>5.0%</b>
    </div>

    <div className={styles.cardRow}>
      <span>{t('Persons with significant control')}</span>
      <b>2</b>
    </div>

    <p className={styles.cardFoot}>
      {t('Sample record')} &middot; {t('from the shareholding and officer filings')}
    </p>
  </div>
);

const ExecutivesCard = ({ t }: { t: Translate }) => (
  <div className={styles.card}>
    <p className={styles.cardKicker}>{t('Executives on file')} &mdash; 27</p>

    {getExecutives(t).map((p) => (
      <div key={p.name} className={styles.exec}>
        <div className={styles.execInfo}>
          <b>{p.name}</b>
          <span>{p.role}</span>
          <em>{p.mail}</em>
          <em>{p.tel}</em>
        </div>

        <div className={styles.execActs}>
          <i>{t('Reveal email')}</i>
          <i>{t('Reveal phone')}</i>
          <i>{t('Add to CRM')}</i>
        </div>
      </div>
    ))}

    <p className={styles.cardFoot}>
      {t('Sample record')} &middot; {t('filterable by role and seniority')}
    </p>
  </div>
);

const PushToCrmCard = ({ t }: { t: Translate }) => (
  <div className={cn(styles.card, styles.pushCard)}>
    <div className={styles.pushHead}>
      <div>
        <p className={styles.cardKicker}>{t('Push to CRM')}</p>
        <p className={styles.cardTitle}>Meridian Logistics Ltd</p>
        <p className={styles.cardMeta}>07734112 &middot; {t('company + 3 contacts')}</p>
      </div>

      <span className={styles.pushBtn}>{t('Push')}</span>
    </div>

    <div className={styles.pushRows}>
      {getCrmRows(t).map((row, i) => (
        <div key={row.name} className={styles.pushRow} style={{ animationDelay: `${i * 0.5}s` }}>
          <div className={styles.pushName}>
            {row.name}
            <span>{row.meta}</span>
          </div>

          <div className={styles.pushStatus}>
            <i className={styles.stQueued}>{t('Queued')}</i>
            <i className={styles.stWriting}>{t('Writing')}</i>
            <i className={styles.stOk}>{t('Created')}</i>
          </div>
        </div>
      ))}
    </div>

    <p className={styles.cardFoot}>
      {t('Matched on')} <b>registration_number</b>
      {t(', so group entities stay distinct.')}
    </p>
  </div>
);

export const getWhyItems = (t: Translate): DifferenceItem[] => [
  {
    number: '01',
    title: t('Confirm the company exists before you spend the effort'),
    description: (
      <p>
        {t(
          'Registration number, VAT number, national tax ID and current registry status, checked against the register that issued them. You find out the entity is dormant, dissolved or unregistered before you write the proposal.',
        )}
      </p>
    ),
    media: <PanelSlide t={t} />,
  },
  {
    number: '02',
    title: t('See the group behind the brand'),
    description: (
      <p>
        {t(
          'A website shows one company. The register shows the parent, the subsidiaries, the shareholders and the persons with significant control — so you know who ultimately owns it.',
        )}
      </p>
    ),
    media: <OwnershipCard t={t} />,
  },
  {
    number: '03',
    title: t('Read the numbers the company filed, not the ones it markets'),
    description: (
      <p>
        {t(
          'Twenty years of filed turnover, profit, assets and headcount where the registry holds them. Enough history to size the account properly before a discovery call.',
        )}
      </p>
    ),
    media: <FinancialsCard t={t} />,
  },
  {
    number: '04',
    title: t('See who signs and who owns it'),
    description: (
      <p>
        {t(
          'Named directors with appointment dates, registered shareholders with holdings, and persons with significant control — read from the filings rather than an about page.',
        )}
      </p>
    ),
    media: <OfficersCard t={t} />,
  },
  {
    number: '05',
    title: t('Reach the person who owns the budget'),
    description: (
      <p>
        {t(
          'Named executives with role and seniority, filterable by function. Reveal verified work email and direct dial, then send from the panel.',
        )}
      </p>
    ),
    media: <ExecutivesCard t={t} />,
  },
  {
    number: '06',
    title: t('Keep what you found'),
    description: (
      <p>
        {t(
          'Push the company and the contact into HubSpot, Salesforce or Microsoft Dynamics, or save to a list. Registry identifiers travel with the record.',
        )}
      </p>
    ),
    media: <PushToCrmCard t={t} />,
  },
];

export const WHY_CHOOSE_GDB_ITEMS: WhoCanBenefitItem[] = [
  {
    icon: 'i-42',
    title: 'Save Time',
    description: 'Access verified data and actionable insights instantly without switching platforms.',
  },
  {
    icon: 'i-57',
    title: 'Make Smarter Decisions',
    description:
      'Combine financial insights, marketing data, and ownership details for a complete view of any company.',
  },
  {
    icon: 'i-8',
    title: 'Engage Effectively',
    description: '- Use direct contact details and personalized outreach to build stronger connections.',
  },
  {
    icon: 'i-160',
    title: 'Boost Productivity',
    description: 'Eliminate manual research and streamline prospecting, validation, and outreach',
  },
];

type InstallStep = {
  n: string;
  title: string;
  desc: string;
  caption: ReactNode;
  img: string;
  alt: string;
  footnote: string;
  bare?: boolean;
};

const getInstallSteps = (t: Translate): InstallStep[] => [
  {
    n: '01',
    title: t('Start the install'),
    desc: t('Opens the Global Database extension listing in the store for your browser.'),
    caption: (
      <>
        {t('Click')} <b>{t('Install for free')}</b> {t('to open the store listing')}
      </>
    ),
    img: '/storage/img/browser-extension/step-01.webp',
    alt: t('The Global Database Extension listing in the Chrome Web Store'),
    footnote: t('Published by Global Data Intelligence Limited.'),
  },
  {
    n: '02',
    title: t('Click Get'),
    desc: t('Published by Global Data Intelligence Limited. Confirm the permission prompt when the browser asks.'),
    caption: (
      <>
        {t('Click')} <b>Get</b>
        {t(', then confirm the browser prompt')}
      </>
    ),
    img: '/storage/img/browser-extension/step-02.webp',
    alt: t('The Get button on the store listing'),
    footnote: t('Published by Global Data Intelligence Limited. The browser asks you to confirm the permissions once.'),
    bare: true,
  },
  {
    n: '03',
    title: t('Enter your email'),
    desc: t('Use a work email address and confirm the link. Google and Microsoft sign-in are both supported.'),
    caption: t('Enter your work email and confirm the link'),
    img: '/storage/img/browser-extension/step-03.webp',
    alt: t('The extension sign-up screen asking for an email address'),
    footnote: t('A work email address, or Google and Microsoft sign-in.'),
  },
  {
    n: '04',
    title: t('Add your contact details'),
    desc: t(
      'Sets up the account the panel signs you into, and the sender identity used when you email a contact from it.',
    ),
    caption: (
      <>
        {t('After you click the')} <b>{t('confirmation link')}</b>{' '}
        {t('in the email, enter your name, company and work email')}
      </>
    ),
    img: '/storage/img/browser-extension/step-04.webp',
    alt: t('The account details form'),
    footnote: t('Used for the account and as the sender identity when you email from the panel.'),
  },
  {
    n: '05',
    title: t('Pin it to the toolbar'),
    desc: t(
      'Open the extensions menu in your browser and pin Global Database, so the icon stays visible on every page.',
    ),
    caption: t('Open the extensions menu and pin Global Database'),
    img: '/storage/img/browser-extension/step-05.webp',
    alt: t('The browser extensions menu with Global Database pinned'),
    footnote: t('Pinning keeps the icon on the toolbar so the panel is one click away.'),
  },
  {
    n: '06',
    title: t('Open any company page'),
    desc: t(
      'Click the icon on a company website, a LinkedIn company page or a personal profile, and the record opens beside it.',
    ),
    caption: t('Click the icon on any company page'),
    img: '/storage/img/browser-extension/step-06.webp',
    alt: t('The panel open beside a company website, showing the registry record'),
    footnote: t(
      'Click the toolbar icon and the registry record opens beside the page. Nothing to search for and nothing to copy across.',
    ),
  },
];

const InstallStepMedia = ({ step }: { step: InstallStep }) => (
  <div className={styles.stepCard}>
    <p className={styles.stepCaption}>{step.caption}</p>

    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img
      src={step.img}
      alt={step.alt}
      loading="lazy"
      className={cn(styles.stepShot, step.bare && styles.stepShotBare)}
    />

    <p className={styles.stepFootnote}>{step.footnote}</p>
  </div>
);

export const getHowToInstallItems = (t: Translate): DifferenceItem[] =>
  getInstallSteps(t).map((step) => ({
    number: step.n,
    title: step.title,
    description: <p>{step.desc}</p>,
    media: <InstallStepMedia step={step} />,
  }));
